/**
 * Item status pill policy: which status set a line's picker offers, which status it is in, and how its pill
 * is coloured on the Items board, the SOR board, SOR Details and the Items (old) table.
 */
import { ActivityEntityName, CodeStatus, EmbeddedCodeLike, EntityStatusType, EntitySubStatus, PickerEntityStatusRequest } from '@jms/model';
import { ItemStatusHelper } from '@jms/utils/util.helper';
import { badgeTint } from '@workbuddy/ui/modules/manage.job.pro/core/src/model';

export interface ItemStatusStyle {
  color: string;
  backgroundColor: string;
  borderColor: string;
}

/**
 * SOR board Normal pill (Figma Jobs 2744:182961): a neutral outlined chip rather than the filled grey
 * the shared priority tokens give. LOCAL STAND-IN — passed per instance because the badge's neutral
 * palette does not carry this yet; fold it into the shared tokens and delete this when it does.
 * Scoped to the SOR mount so the Items Pro board keeps the palette it has always had.
 */
const sorNormalStyle: ItemStatusStyle = { color: '#414651', backgroundColor: '#fafafa', borderColor: 'var(--action-secondary-color-neutral-default-border, #d6d8db)' };

/** Inline editors expose active field edits only; inactive lifecycle changes are row commands. */
export function resolveActiveItemStatusPickerOptions(isClaimCode: boolean): PickerEntityStatusRequest {

  return { entityType: isClaimCode ? ActivityEntityName.ItemPending : ActivityEntityName.Item, displayType: EntityStatusType.Active, restrictToSelectable: true };
}

/**
 * Prefer entity-status bridge (`itemStatus`) when present. SOR and pending rows use it exclusively;
 * ordinary pre-migration Item rows fall back to their legacy CodeStatus.
 */
export function resolveEffectiveStatus(status: CodeStatus | null, itemStatus: EntitySubStatus | null): CodeStatus | null {

  return (itemStatus?.legacyStatus ?? itemStatus?.systemName ?? status) as CodeStatus | null;
}

/**
 * Status pill styling. A line's status is saved with its name and colour together, so the pill draws the
 * colour saved on the line — not a live Settings lookup, which would drift from the saved name (WBS-13023).
 * A line with no saved colour keeps the CodeStatus palette, from the shared badge priority tokens.
 * Called by the board status cell and the SOR/Item Details header, on office and Contractor Portal mounts.
 */
export function resolveStatusStyle(status: CodeStatus | null, itemStatus: EntitySubStatus | null): ItemStatusStyle {

  const tint = badgeTint(itemStatus?.color);
  if (tint) return { color: tint.text, backgroundColor: tint.background, borderColor: tint.border };

  const effectiveStatus = resolveEffectiveStatus(status, itemStatus);

  switch (effectiveStatus) {
    case CodeStatus.Cancelled:
    case CodeStatus.Declined:
    case CodeStatus.Deleted:
      return { color: 'var(--badge-color-priority-p1-text, #991B1B)', backgroundColor: 'var(--badge-color-priority-p1-background, #FECACA)', borderColor: 'transparent' };

    case CodeStatus.Variation:
      return { color: 'var(--badge-color-priority-p3-text, #92400E)', backgroundColor: 'var(--badge-color-priority-p3-background, #FDE68A)', borderColor: 'transparent' };

    case CodeStatus.Requested:
    case CodeStatus.Pending:
    case CodeStatus.PendingReview:
      return { color: 'var(--badge-color-priority-p4-text, #1E40AF)', backgroundColor: 'var(--badge-color-priority-p4-background, #BFDBFE)', borderColor: 'transparent' };

    default:
      return { color: 'var(--badge-color-priority-p6-text-subtle, #6B7280)', backgroundColor: 'var(--badge-color-priority-p6-background, #E5E7EB)', borderColor: 'transparent' };
  }
}

/** The SOR board's outlined Normal chip keeps its shape; only its text and outline take the saved colour. */
export function resolveSorNormalStyle(itemStatus: EntitySubStatus | null): ItemStatusStyle {

  const tint = badgeTint(itemStatus?.color);

  return tint ? { ...sorNormalStyle, color: tint.text, borderColor: tint.border } : sorNormalStyle;
}

/**
 * Items (old) pill colours. A pending line is labelled with its saved status, so it draws that status's
 * saved colour; an authorised line is labelled with its CodeStatus, so it keeps the CodeStatus colours.
 * Called by the office Items (old) table and card, the Claims tab line, and the Contractor Portal Items (old) lines.
 */
export function resolveLegacyTableStatusColors(item: EmbeddedCodeLike): { background: string; foreground: string } {

  const tint = item?.isClaimCode ? badgeTint(item.itemStatus?.color) : null;

  return tint ? { background: tint.background, foreground: tint.text } : ItemStatusHelper.itemStatusColor(item);
}
